import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Tables } from "@how/database";
import type { SemanticRole } from "@how/design-system";
import type { ConsentFilter } from "./consent-queries";

export type PersonRow = Pick<
  Tables<"people">,
  "id" | "first_name" | "last_name" | "preferred_name" | "birthdate" | "created_at"
> & {
  /** The primary email and phone, for the list; the drawer shows every contact method. */
  readonly email: string | null;
  readonly phone: string | null;
};

type ContactRow = Pick<Tables<"person_contacts">, "method" | "value" | "is_primary">;

/** The primary contact of a method, or the first one when none is marked primary. */
function primaryOf(contacts: readonly ContactRow[], method: ContactRow["method"]): string | null {
  const ofMethod = contacts.filter((c) => c.method === method);
  return (ofMethod.find((c) => c.is_primary) ?? ofMethod[0])?.value ?? null;
}

export type PersonSort = {
  readonly id: "last_name" | "created_at";
  readonly desc: boolean;
};

export interface PeopleQuery {
  readonly search: string;
  readonly pageIndex: number;
  readonly pageSize: number;
  readonly sort: PersonSort;
  /** Only people carrying this tag. */
  readonly tagId?: string;
  /** Only the people behind one number on the consent screen (#112). */
  readonly consent?: ConsentFilter;
  /** Only the people who can, or cannot, sign in to the member portal as themselves (#136). */
  readonly portalLogin?: "yes" | "no";
}

/** The tenant id for the host this app is being served on, or null while it resolves. */
function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** Escapes the characters PostgREST's `ilike` treats as wildcards, so a search for "50%" is a literal. */
function likeTerm(search: string): string | null {
  const trimmed = search.trim();
  if (trimmed.length === 0) return null;
  return `%${trimmed.replace(/[%_]/g, "")}%`;
}

/**
 * The people list: filtered, sorted and paginated by the server, never in the browser. Row-level security is the
 * boundary; the explicit church filter below is what keeps a reader of two churches to the one on screen.
 */
export function usePeople(q: PeopleQuery) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["people", tenantId, q],
    enabled: tenantId !== null,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: PersonRow[]; total: number }> => {
      const from = q.pageIndex * q.pageSize;
      // Filtering by tag is an inner join on the link table, so the count is of matching people rather than of
      // everyone. Two literal select strings rather than one built at runtime, because the client infers the row
      // type from the literal and a computed string would lose it.
      //
      // A consent filter starts from people_with_consent, which returns people rows from the one definition the
      // consent screen counts with, so everything after this point, and the count, is the same either way.
      const people = supabase.from("people");
      const consented =
        q.consent === undefined
          ? null
          : supabase.rpc(
              "people_with_consent",
              {
                p_tenant_id: tenantId ?? "",
                p_channel: q.consent.channel,
                p_state: q.consent.state,
              },
              { count: "exact" },
            );
      let query = (
        consented !== null
          ? q.tagId === undefined
            ? consented.select(
                "id, first_name, last_name, preferred_name, birthdate, created_at, person_contacts(method, value, is_primary)",
              )
            : consented
                .select(
                  "id, first_name, last_name, preferred_name, birthdate, created_at, person_contacts(method, value, is_primary), person_tags!inner(tag_id)",
                )
                .eq("person_tags.tag_id", q.tagId)
          : q.tagId === undefined
            ? people.select(
                "id, first_name, last_name, preferred_name, birthdate, created_at, person_contacts(method, value, is_primary)",
                { count: "exact" },
              )
            : people
                .select(
                  "id, first_name, last_name, preferred_name, birthdate, created_at, person_contacts(method, value, is_primary), person_tags!inner(tag_id)",
                  { count: "exact" },
                )
                .eq("person_tags.tag_id", q.tagId)
      )
        // Row-level security admits every church the reader belongs to, not only the one on screen, so a staff member
        // of two churches would otherwise see both directories merged. The policies stay the security boundary; this
        // filter is what makes the list mean "this church".
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order(q.sort.id, { ascending: !q.sort.desc })
        // A family sorts by first name within its surname, the way anyone reading a directory expects, and the id
        // breaks any remaining tie so paging is stable.
        .order("first_name", { ascending: true })
        .order("id", { ascending: true })
        .range(from, from + q.pageSize - 1);
      // Who can already sign in is the question a church asks first (#136): a filter, as Planning Center's lists do.
      if (q.portalLogin === "yes") query = query.not("auth_user_id", "is", null);
      if (q.portalLogin === "no") query = query.is("auth_user_id", null);
      const term = likeTerm(q.search);
      if (term !== null) {
        query = query.or(
          `first_name.ilike.${term},last_name.ilike.${term},preferred_name.ilike.${term}`,
        );
      }
      const { data, error, count } = await query;
      if (error) throw error;
      return {
        rows: data.map((row) => ({
          id: row.id,
          first_name: row.first_name,
          last_name: row.last_name,
          preferred_name: row.preferred_name,
          birthdate: row.birthdate,
          created_at: row.created_at,
          email: primaryOf(row.person_contacts, "email"),
          phone: primaryOf(row.person_contacts, "phone"),
        })),
        total: count ?? 0,
      };
    },
  });
}

export interface PersonDetail {
  readonly person: Tables<"people">;
  readonly contacts: readonly Pick<
    Tables<"person_contacts">,
    "id" | "method" | "value" | "label" | "is_primary" | "verified_at"
  >[];
  readonly households: readonly { readonly name: string; readonly role: string }[];
  readonly tags: readonly {
    readonly id: string;
    /** The person-to-tag link, which is what removing a tag from this person deletes. */
    readonly personTagId: string;
    readonly name: string;
    /** A design-system role name, resolved to a colour at render time. */
    readonly color: SemanticRole;
  }[];
  readonly notes: readonly Pick<
    Tables<"person_notes">,
    "id" | "body" | "created_at" | "is_confidential"
  >[];
  readonly activities: readonly Pick<
    Tables<"activities">,
    "id" | "kind" | "summary" | "occurred_at"
  >[];
  /** Every field the church defines, whether or not this person has a value in it, in the church's order. */
  readonly fields: readonly {
    readonly definition: Pick<
      Tables<"custom_field_definitions">,
      "id" | "key" | "label" | "field_type" | "options" | "help_text"
    >;
    readonly value: Pick<
      Tables<"custom_field_values">,
      "value_text" | "value_number" | "value_date" | "value_boolean" | "value_options"
    > | null;
  }[];
  readonly preferences: readonly Pick<
    Tables<"communication_preferences">,
    "channel" | "opted_in" | "source" | "recorded_at"
  >[];
}

/**
 * Everything the record drawer shows, in one round trip's worth of parallel requests. Notes come back only for a
 * caller holding `notes.read`, and a confidential note only with `notes.read_confidential`: the policies decide, so
 * this component does not need to know the rules.
 */
export function usePerson(personId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["person", tenantId, personId],
    enabled: personId !== null,
    queryFn: async (): Promise<PersonDetail> => {
      const id = personId ?? "";
      const [
        person,
        contacts,
        memberships,
        tags,
        notes,
        activities,
        definitions,
        values,
        preferences,
      ] = await Promise.all([
        supabase.from("people").select("*").eq("id", id).single(),
        supabase
          .from("person_contacts")
          .select("id, method, value, label, is_primary, verified_at")
          .eq("person_id", id)
          .order("is_primary", { ascending: false }),
        supabase.from("household_memberships").select("role, households(name)").eq("person_id", id),
        supabase
          .from("person_tags")
          .select("id, tag_id, tags(id, name, color_role)")
          .eq("person_id", id),
        supabase
          .from("person_notes")
          .select("id, body, created_at, is_confidential")
          .eq("person_id", id)
          .is("deleted_at", null)
          .order("created_at", { ascending: false }),
        supabase
          .from("activities")
          .select("id, kind, summary, occurred_at")
          .eq("person_id", id)
          .order("occurred_at", { ascending: false })
          .limit(25),
        supabase
          .from("custom_field_definitions")
          .select("id, key, label, field_type, options, help_text")
          // Only this church's fields: without it a reader of two churches got both churches' fields on one record.
          .eq("tenant_id", tenantId ?? "")
          .order("sort_order")
          .order("label"),
        supabase
          .from("custom_field_values")
          .select(
            "definition_id, value_text, value_number, value_date, value_boolean, value_options",
          )
          .eq("person_id", id),
        supabase
          .from("communication_preferences")
          .select("channel, opted_in, source, recorded_at")
          .eq("person_id", id),
      ]);
      if (person.error) throw person.error;
      if (contacts.error) throw contacts.error;
      if (memberships.error) throw memberships.error;
      if (tags.error) throw tags.error;
      if (activities.error) throw activities.error;
      if (definitions.error) throw definitions.error;
      if (values.error) throw values.error;
      if (preferences.error) throw preferences.error;
      const valueFor = new Map(values.data.map(({ definition_id: key, ...value }) => [key, value]));
      // Notes are the one part a reader may legitimately lack: treat a denial as "none visible" rather than an error
      // for the whole drawer, so a staff member without notes.read still sees the record.
      return {
        person: person.data,
        contacts: contacts.data,
        // Both embedded resources hang off a non-null foreign key, so neither can come back missing.
        households: memberships.data.map((row) => ({
          name: row.households.name,
          role: row.role,
        })),
        tags: tags.data.map((row) => ({
          id: row.tags.id,
          personTagId: row.id,
          name: row.tags.name,
          // The column's check constraint admits exactly the design-system role names.
          color: row.tags.color_role as SemanticRole,
        })),
        notes: notes.error ? [] : notes.data,
        activities: activities.data,
        fields: definitions.data.map((definition) => ({
          definition,
          value: valueFor.get(definition.id) ?? null,
        })),
        preferences: preferences.data,
      };
    },
  });
}

/** Invalidates both the list and the open record, so a write is visible without a reload. */
export function usePeopleInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (personId?: string) => {
    await client.invalidateQueries({ queryKey: ["people", tenantId] });
    if (personId !== undefined) {
      await client.invalidateQueries({ queryKey: ["person", tenantId, personId] });
    }
  };
}

export interface NewPerson {
  readonly firstName: string;
  readonly lastName: string;
  readonly preferredName?: string;
  readonly email?: string;
}

/** Creates a person and, when one was given, their first email in the same flow. */
export function useCreatePerson() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = usePeopleInvalidation();
  return useMutation({
    mutationFn: async (input: NewPerson): Promise<string> => {
      const { data, error } = await supabase.rpc("create_person", {
        p_tenant_id: tenantId ?? "",
        p_first_name: input.firstName,
        p_last_name: input.lastName,
        ...(input.preferredName === undefined ? {} : { p_preferred_name: input.preferredName }),
      });
      if (error) throw error;
      const personId = data;
      if (input.email !== undefined && input.email.length > 0) {
        const contact = await supabase.rpc("set_person_contact", {
          p_person_id: personId,
          p_method: "email",
          p_value: input.email,
          p_is_primary: true,
        });
        if (contact.error) throw contact.error;
      }
      return personId;
    },
    onSuccess: async (personId) => {
      await invalidate(personId);
    },
  });
}

export function useAddNote() {
  const supabase = useSupabase();
  const invalidate = usePeopleInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly personId: string;
      readonly body: string;
      readonly confidential: boolean;
    }) => {
      const { error } = await supabase.rpc("add_person_note", {
        p_person_id: input.personId,
        p_body: input.body,
        p_is_confidential: input.confidential,
      });
      if (error) throw error;
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.personId);
    },
  });
}

/**
 * The audited export (section 16.4). The rows come back from the function rather than from the list query, because
 * the function is what writes the audit row recording who exported what and how many.
 */
export function useExportPeople() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async (search: string): Promise<string> => {
      const { data, error } = await supabase.rpc("export_people", {
        p_tenant_id: tenantId ?? "",
        ...(search.trim().length === 0 ? {} : { p_search: search.trim() }),
      });
      if (error) throw error;
      const header = [
        "First name",
        "Last name",
        "Preferred name",
        "Birthdate",
        "Email",
        "Phone",
        "Household",
        "Tags",
      ];
      const cell = (value: string | null): string =>
        value === null ? "" : `"${value.replace(/"/g, '""')}"`;
      const lines = [
        header.join(","),
        ...data.map((row) =>
          [
            row.first_name,
            row.last_name,
            row.preferred_name,
            row.birthdate,
            row.primary_email,
            row.primary_phone,
            row.household_name,
            row.tags,
          ]
            .map((value) => cell(value as string | null))
            .join(","),
        ),
      ];
      return lines.join("\n");
    },
  });
}

// ------------------------------------------------------------------------------------------ record edits

/** Every edit to a person refreshes the record and the list, since the list shows name, email and phone. */
export function usePersonWrite<TInput extends { readonly personId: string }>(
  // The Supabase builder is thenable rather than a Promise, so the signature asks only for what await needs.
  write: (input: TInput) => PromiseLike<{ error: { message: string; code?: string } | null }>,
) {
  const invalidate = usePeopleInvalidation();
  return useMutation({
    mutationFn: async (input: TInput) => {
      const { error } = await write(input);
      // A real Error for the stack trace, still carrying the Postgres code a caller may branch on.
      if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.personId);
    },
  });
}

export interface PersonFields {
  readonly firstName: string;
  readonly lastName: string;
  readonly preferredName: string;
  /** ISO date or empty. */
  readonly birthdate: string;
  /** The person's home campus (ADR 0036), or empty for none. */
  readonly primaryCampusId: string;
}

export function useUpdatePerson() {
  const supabase = useSupabase();
  return usePersonWrite((input: { readonly personId: string; readonly fields: PersonFields }) => {
    const preferred = input.fields.preferredName.trim();
    const birthdate = input.fields.birthdate.trim();
    const campus = input.fields.primaryCampusId.trim();
    return supabase.rpc("update_person", {
      p_person_id: input.personId,
      p_first_name: input.fields.firstName.trim(),
      p_last_name: input.fields.lastName.trim(),
      ...(preferred.length === 0 ? {} : { p_preferred_name: preferred }),
      ...(birthdate.length === 0 ? {} : { p_birthdate: birthdate }),
      ...(campus.length === 0 ? {} : { p_primary_campus_id: campus }),
    });
  });
}

export function useDeletePerson() {
  const supabase = useSupabase();
  return usePersonWrite((input: { readonly personId: string }) =>
    supabase.rpc("delete_person", { p_person_id: input.personId }),
  );
}

export function useSetContact() {
  const supabase = useSupabase();
  return usePersonWrite(
    (input: {
      readonly personId: string;
      readonly method: "email" | "phone";
      readonly value: string;
      readonly label: string;
      readonly primary: boolean;
    }) =>
      supabase.rpc("set_person_contact", {
        p_person_id: input.personId,
        p_method: input.method,
        p_value: input.value.trim(),
        ...(input.label.trim().length === 0 ? {} : { p_label: input.label.trim() }),
        p_is_primary: input.primary,
      }),
  );
}

export function useRemoveContact() {
  const supabase = useSupabase();
  return usePersonWrite((input: { readonly personId: string; readonly contactId: string }) =>
    supabase.rpc("remove_person_contact", { p_contact_id: input.contactId }),
  );
}

export type CustomValue =
  | { readonly p_text: string }
  | { readonly p_number: number }
  | { readonly p_date: string }
  | { readonly p_boolean: boolean }
  | { readonly p_options: string[] }
  /** No value at all clears the field. */
  | Record<string, never>;

export function useSetCustomValue() {
  const supabase = useSupabase();
  return usePersonWrite(
    (input: {
      readonly personId: string;
      readonly definitionId: string;
      readonly value: CustomValue;
    }) =>
      supabase.rpc("set_custom_field_value", {
        p_person_id: input.personId,
        p_definition_id: input.definitionId,
        ...input.value,
      }),
  );
}

export function useSetPreference() {
  const supabase = useSupabase();
  return usePersonWrite(
    (input: {
      readonly personId: string;
      readonly channel: "email" | "sms" | "phone" | "mail";
      readonly optedIn: boolean;
    }) =>
      supabase.rpc("set_communication_preference", {
        p_person_id: input.personId,
        p_channel: input.channel,
        p_opted_in: input.optedIn,
        // The evidence of where consent came from: a staff member recorded it in the dashboard.
        p_source: "Recorded by staff in the dashboard",
      }),
  );
}
